import React from 'react';
import { ActionIcon, Checkbox, Popover, Stack, Text, Tooltip } from '@mantine/core';
import { Columns } from '@phosphor-icons/react';

/**
 * Show / hide columns on a DataSheet grid.
 *
 * Lifted out of the Inventory container so every grid offering the control renders the
 * same popover rather than growing its own. Visibility is expressed as the set of HIDDEN
 * keys, matching how the grids already thread `hiddenColumns` down to their column
 * builders — a column absent from `hiddenKeys` shows.
 *
 * `defaultOnDefs` / `defaultOffDefs` only describe intent for the caller seeding its
 * initial hidden set; both render in one list, in the order given.
 */

export type ColumnPickerDef = { key: string; label: string };

export interface ColumnPickerProps {
    /** Columns that ship visible — listed first. */
    defaultOnDefs: ColumnPickerDef[];
    /** Columns that ship hidden until the operator asks for them. */
    defaultOffDefs: ColumnPickerDef[];
    hiddenKeys: string[];
    onChange: (hiddenKeys: string[]) => void;
    /** Entity-attribute columns, listed under their own heading. Opt-in, not hide-out. */
    attrDefs?: ColumnPickerDef[];
    visibleAttrKeys?: string[];
    onAttrChange?: (visibleKeys: string[]) => void;
}

export const ColumnPicker: React.FC<ColumnPickerProps> = ({
    defaultOnDefs,
    defaultOffDefs,
    hiddenKeys,
    onChange,
    attrDefs = [],
    visibleAttrKeys = [],
    onAttrChange,
}) => {
    const hiddenSet = new Set(hiddenKeys);

    const toggleStatic = (key: string, checked: boolean) => {
        if (checked) {
            onChange(hiddenKeys.filter((k) => k !== key));
        } else {
            onChange([...hiddenKeys, key]);
        }
    };

    const toggleAttr = (key: string, checked: boolean) => {
        if (!onAttrChange) return;
        if (checked) {
            onAttrChange([...visibleAttrKeys, key]);
        } else {
            onAttrChange(visibleAttrKeys.filter((k) => k !== key));
        }
    };

    const allStaticDefs = [...defaultOnDefs, ...defaultOffDefs];

    return (
        <Popover position="bottom-end" withArrow shadow="md">
            <Popover.Target>
                <Tooltip label="Columns" withArrow>
                    <ActionIcon variant="subtle" color="gray" size="sm">
                        <Columns size={16} />
                    </ActionIcon>
                </Tooltip>
            </Popover.Target>
            <Popover.Dropdown>
                <Stack gap="xs">
                    <Text size="xs" fw={600} c="dimmed">
                        Show / Hide Columns
                    </Text>
                    {allStaticDefs.map((def) => (
                        <Checkbox
                            key={def.key}
                            label={def.label}
                            size="xs"
                            color="violet"
                            checked={!hiddenSet.has(def.key)}
                            onChange={(e) =>
                                toggleStatic(def.key, e.currentTarget.checked)
                            }
                        />
                    ))}
                    {attrDefs.length > 0 && (
                        <>
                            <Text size="xs" fw={600} c="dimmed" mt={4}>
                                Attributes
                            </Text>
                            {attrDefs.map((def) => (
                                <Checkbox
                                    key={def.key}
                                    label={def.label}
                                    size="xs"
                                    color="violet"
                                    checked={visibleAttrKeys.includes(def.key)}
                                    onChange={(e) =>
                                        toggleAttr(
                                            def.key,
                                            e.currentTarget.checked,
                                        )
                                    }
                                />
                            ))}
                        </>
                    )}
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};

export default ColumnPicker;
